import type { TransactionGateway } from "@/lib/types"
import type { UseFormReturn } from "react-hook-form"

// types/formTypes.ts
export interface FormConfig {
	form_id: string
	name: string
	description?: string
	theme?: Record<string, string>
	show_close_icon?: boolean
	type: FormType
	associated_form_id?: string
	widget_id?: string
	university_id?: string
	batch_id?: string
	status: string
	partner_id: string
	is_lead_punch?: boolean
	is_referral_widget?: boolean
	source?: string

	// Stage styling
	stages: STAGE[]
	stages_configuration: StageConfiguration
	condition_logic_url?: string
}

// Defines how stage headers look/align
export interface StageConfiguration {
	type: "TITLES_ONLY" | "TITLES_AND_DESCRIPTIONS"
	alignment: "TOP" | "LEFT" | "BOTTOM" | "RIGHT"
}

// Stage contains multiple sections
export interface STAGE {
	stage_id: string
	name: string
	description?: string
	order_index: number // on hold check after reply
	sections: SECTION[]
}

export type FieldType =
	| "TEXT"
	| "EMAIL"
	| "PASSWORD"
	| "NUMBER"
	| "DATE"
	| "TEXT_AREA"
	| "CHECKBOX"
	| "RADIO"
	| "FILE"
	| "TEL"
	| "BUTTON" // New field type
	| "PARAGRAPH" // New field type
	| "MULTI_CHECKBOX"
	| "SELECT"
	| "MULTI_SELECT"
	| "TABS"
	| "BUTTON"
	| "PAYMENT_ONE"
	| "PAYMENT_TWO"
	| "AADHAR"
	| "PROGRAM_TYPE"
	| "PROGRAM"
	| "SPECIALIZATION"
	| "RICH_TEXT"
	| "COUNTRY"
	| "STATE"
	| "ADDRESS"
	| "POSTAL_CODE"
	| "ACKNOWLEDGEMENT"
	| "DEB_ID"
	| "ABC_ID"

export interface SECTION {
	section_id: string
	order_index: number
	rows: ROW[]
	attributes: Array<Attribute>
}

export interface ROW {
	// row_id?: string
	order_index: number
	columns: COLUMN[]
}

export interface COLUMN {
	// column_id: string
	order_index: number
	fields: FIELD[]
}

export interface FIELD {
	field_id: string
	order_index: number
	field_type: FieldType
	// label: string
	// placeholder?: string
	is_required: boolean
	require_verification?: boolean
	// default_value?: string
	// options?: { label: string; value: string }[]
	// select_all?: boolean
	validations?: Validation[]
	conditions?: Condition[]

	// // Additional properties for new field types
	// button_type?: "button" | "submit" | "reset"
	// variant?:
	// | "default"
	// | "secondary"
	// | "outline"
	// | "ghost"
	// | "link"
	// | "destructive"
	// size?: "default" | "sm" | "lg"
	// content?: string // For paragraph fields
	// links?: Array<{
	// 	// For paragraph fields with links
	// 	text: string
	// 	url: string
	// 	target?: "_blank" | "_self"
	// }>
	// onClick?: () => void // For button fields
	// disabled?: boolean
	// className?: string
	attributes: Array<Attribute>
}

// Possible attribute data types
type AttributeDataType =
	| "STRING"
	| "BOOLEAN"
	| "NUMBER"
	| "ARRAY"
	| "OBJECT"
	| "DATE"

// Mapping from data_type to actual TypeScript type
type AttributeValueMap = {
	STRING: string
	BOOLEAN: boolean
	NUMBER: number

	ARRAY: any[] // Could make this more specific if you know the array structure

	OBJECT: any
	DATE: Date
}

export enum CommonFieldConfig {
	Label = "label",
	Description = "description",
	IsRequired = "is_required",
	// ConditionalLogic = "Conditional _Logic",
	InfoIconRequired = "info_icon_required",
	InfoIconType = "info_icon_type",
	InfoIconText = "info_icon_text",
	ReadOnly = "read_only",
	Error = "error",
	Alias = "alias",
}

export enum TextInputConfig {
	PlaceholderText = "placeholder",
	CharacterLimit = "character_limit",
	InputType = "input_type",
	CaseFormatting = "case_formatting",
}

export enum NumberInputConfig {
	MinValue = "min_value",
	MaxValue = "max_value",
	DecimalAllowed = "decimal_allowed",
	Units = "units",
	PlaceholderText = "placeholder",
}

export enum EmailInputConfig {
	DomainRestriction = "domain_restriction",
	ValidateWithOTP = "validate_otp",
	ResendOTPTimer = "timer",
}

export enum DropdownConfig {
	OptionList = "options",
	AllowOtherOption = "allow_other",
	TextInputRequired = "allow_other_input",
	DefaultSelection = "default_selection",
	SortingOfOptions = "sorting",
	// DependentDropdown = "dependent_dropdown",
}

export enum TextAreaConfig {
	PlaceholderText = "placeholder",
	CharacterLimitMin = "character_limit_min",
	CharacterLimitMax = "character_limit_max",
}

export enum DateInputConfig {
	MinDate = "min_date",
	MaxDate = "max_date",
	DateFormat = "date_format",
}

export enum RadioConfig {
	Options = "options",
	DefaultOption = "default_selection",
	DisplayStyle = "display_style",
}

export enum CheckboxConfig {
	Options = "options",
	DefaultOptions = "default_selection",
	DisplayStyle = "display_style",
}

export enum FileUploadConfig {
	AllowedFileTypes = "allowed_file_types",
	MaxFileSizeMB = "max_file_size_mb",
}

export enum PhoneNumberInputConfig {
	ValidateWithOTP = "validate_otp",
	ResendOTPTimer = "timer",
}

export enum SectionConfig {
	Name = "name",
	IsCollapsible = "is_collapsible",
}

export enum PaymentOneConfig {
	FeeCategory = "fee_category",
	SummaryTitle = "summary_title",
	NameYourPaymentPlan = "name_your_payment_plan",
	Description = "description",
	ProgramFieldId = "program_field_id",
	SpecializationFieldId = "specialization_field_id",
	NationalityFieldId = "country_field_id",
}

export enum PaymentTwoConfig {
	Self = "self",
	Loan = "loan",
	SelfPlanTitles = "self_plan_titles",
	LoanPlanTitles = "loan_plan_titles",
	NameYourPayment = "name_your_payment",
	Description = "description",
	EnableOfflinePayment = "enable_offline_payment",
	EnableOfflineLoan = "enable_offline_loan",
	DisplayTheSelfAndLoanPaymentModesTogether = "display_the_self_and_loan_payment_modes_together",
	ProgramFieldId = "program_field_id",
	SpecializationFieldId = "specialization_field_id",
	NationalityFieldId = "country_field_id",
	RegistrationFieldId = "registration_field_id",
	PeriodNumber = "period_number",
}

export enum ProgramFieldConfig {
	ProgramTypeFieldId = "program_type_field_id",
}

export enum SpecializationFieldConfig {
	ProgramTypeFieldId = "program_type_field_id,",
	ProgramFieldId = "program_field_id",
}

export enum AddressFieldConfig {
	placeholderText = "placeholder",
	countryFieldId = "country_field_id",
	StateFieldId = "state_field_id",
	CityFieldId = "city_field_id",
	PostalCodeFieldId = "postalcode_field_id",
	LandMarkField = "landmark_field_id",
}

export enum RichTextConfig {
	Content = "content",
}

export enum StateFieldConfig {
	CountryFieldId = "country_field_id",
}

export enum DebIdFieldConfig {
	Placeholder = "placeholder",
	ABCFieldId = "abc_field_id",
	NameFieldId = "deb_name_field_id",
	EmailFieldId = "deb_email_field_id",
	MobileFieldId = "deb_mobile_field_id",
	GenderFieldId = "deb_gender_field_id",
	DOBFieldId = "deb_dob_field_id",
	IsDisabled = "is_disabled",
}

export enum AcknowledgeConfig {
	DefaultSelection = "default_selection",
}

type FieldConfigName =
	| CommonFieldConfig
	| TextInputConfig
	| TextAreaConfig
	| NumberInputConfig
	| EmailInputConfig
	| DropdownConfig
	| DateInputConfig
	| RadioConfig
	| CheckboxConfig
	| FileUploadConfig
	| PaymentOneConfig
	| PaymentTwoConfig
	| PhoneNumberInputConfig
	| RichTextConfig
	| AddressFieldConfig
	| AddressFieldConfig
	| ProgramFieldConfig
	| SpecializationFieldConfig
	| AcknowledgeConfig

export interface Attribute<T extends AttributeDataType = AttributeDataType> {
	name: FieldConfigName
	data_type: T
	value: AttributeValueMap[T]
	attribute_id: number
}

export interface Validation {
	validation_type: "REGEX"
	value: string
	error_message: string
}

export interface Condition {
	effect: "SHOW" | "HIDE"
	logic_operator: "AND" | "OR"
	rules: Rule[]
}

export interface Rule {
	depends_on: string
	operator: "=" | "!="
	value: string | boolean
}

// Additional utility types for React components
export interface FormData {
	[fieldName: string]: unknown
}

export interface ValidationError {
	fieldName: string
	message: string
	stageIndex: number
}

export interface FormState {
	currentStageIndex: number
	formData: FormData
	errors: ValidationError[]
	isSubmitting: boolean
	isDirty: boolean
	isValid: boolean
}

export type Program = {
	id: string
	name: string
}

export type Specialization = {
	id: string
	name: string
	programId: string
}

// // Hook return types
// export interface UseFormReturn {
// 	formState: FormState
// 	goToStage: (index: number) => void
// 	updateField: (name: string, value: unknown) => void
// 	validateForm: () => ValidationError[]
// 	resetForm: () => void
// 	submitForm: () => Promise<void>
// }

// Component prop types
export interface BaseFieldProps {
	field: FIELD
	value?: unknown
	onChange: (value: unknown) => void
	onBlur?: () => void
	error?: string
	disabled?: boolean
	className?: string
	formData?: Record<string, unknown>
}

export interface StageNavigatorProps {
	stages: STAGE[]
	activeIndex: number
	config?: StageConfiguration
	onStageChange: (index: number) => void
	formData?: Record<string, unknown>
}

export type Lead = {
	status: boolean
	lead_id: string
}

export type LEAD_DETAILS = {
	first_name: string
	last_name?: string
	partner_id: string
	counsellor_id?: string
	email: string
	country_code: string
	phone: string
	lead_id: string
	widget_id: string
	gender?: string
	dob?: string
}

export type FormType = "FORM" | "WIDGET" | "DEFAULT_WIDGET"

export interface CommonFieldProps {
	label: string
	required: boolean
	description: string
	infoIconRequired: boolean
	infoIconType: string
	infoIconText: string
	readOnly: boolean
}

export interface FieldProps {
	form: UseFormReturn
	field: FIELD
	commonAttributes: CommonFieldProps
}

export interface COUNTRIES_LISINTG {
	data: COUNTRY[]
}

export interface STATES_LISTING {
	data: STATE[]
}

export interface STATE {
	name: string
	state_code: string
}

export interface COUNTRY {
	name: string
	iso2: string
	iso3: string
}

export interface FEE_ITEM {
	revision_id: string
	dimension_id: string
	component_id: string
	is_course_component: boolean
	name: string
	description: string
	// refundable?: boolean
	frequency: string
	nationality_id: string
	pay_at: string
	currency: string
	price_details: Array<PRICE_DETAIL_ITEM>

	//added for internal use
	discount?: number
	finalPrice: number
	coupon_discount?: number
	price?: number
	isDiscountComponent?: boolean
}

export type PRICE_DETAIL_ITEM = {
	price_id: string
	price: number
	period_number: number
	final_price: number
	discount_id: string
	discount: number
	type: string
}

export type DEB_ID_DETAILS = {
	Message: string
	Status: string
	Resource: [
		{
			ABCID: string
			stdname: string
			email: string
			mobile: string
			Gender: string
			DOB: string
		},
	]
}
export interface DISCOUNT_ITEM {
	component_id: string
	name: string
	description: string
	is_course_component: boolean
	dimension_id: string
	nationality_id: string
	currency: string
	frequency: string
	discount_id: string
	discount: number
	type: string
	period_number: number
	revision_id: string
}

export type ApplicationDiscount = {
	application_id: string
	updated_by: string
	discount: number
	currency: string
	updated_at: string
}
export interface FEE_GRID_RESPONSE {
	course_id: string
	components: FEE_ITEM[]
	// discounts?: DISCOUNT_ITEM[]
	application_discount?: ApplicationDiscount
	coupon_discount?: COUPON_DISCOUNT
	is_last_semester?: boolean
}

export type COUPON_PAYMENT_PLAN = "SEMESTER" | "YEARLY" | "ONE_TIME" | "LOAN"
export type PAYMENT_PLAN = "PER_SEMESTER" | "PER_YEAR" | "ONE_TIME"
export type DISCOUNT_TYPE = "PERCENTAGE" | "FIXED"

export interface COUPON_DISCOUNT {
	plan: COUPON_PAYMENT_PLAN
	payment_mode: "SELF" | "LOAN"
	coupon_id: string
	discount_type: DISCOUNT_TYPE
	discount: number
	schema_id: string
	applied_by: string
	lender_id?: string
	tenure_in_months?: number
}

export interface GET_PAYMENT_STATUS_RESPONSE {
	status: string
	payment_details: PAYMENT_DETAILS
}

export interface PAYMENT_DETAILS {
	order_id: string
	transaction_id: string
	updated_at?: string
	amount: number
	currency: string
}

export interface ORDER_CREATION_RESPONSE {
	payment_required: boolean
	order: ORDER_RESPONSE
}

export interface ORDER_RESPONSE {
	order_id: string
	invoice_number: string
	application_id: string
	batch_id: string
	course_id: string
	gross_amount: number
	net_amount: number
	adjustment_amount: number
	currency: string
	category: string
	plan: string
	status: OrderStatus
	period_number: number
	created_by: string
	created_at: string
	updated_at: string
	items: ORDER_ITEM_RESPONSE[]
}

export interface ORDER_ITEM_RESPONSE {
	order_item_id: string
	fee_component_id: string
	component_dimension_id: string
	amount: number
	currency: string
	period_type: string
	period_number: number
	is_paid: boolean
	paid_at: string
}

export interface CREATE_TRANSACTION_RESPONSE {
	transaction_id: string
	amount: number
	currency: string
	gateway_order_id?: string
	gateway_transaction_id?: string
	gateway: TransactionGateway
	data?: Record<string, string>
}

// Payment lifecycle statuses
export enum OrderStatus {
	IDLE = "IDLE",
	PENDING = "PENDING",
	CREATED = "CREATED",
	PAID = "PAID",
	FAILED = "FAILED",
	CANCELLED = "CANCELLED",
	PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
	FULLY_REFUNDED = "FULLY_REFUNDED",
	ADJUSTED = "ADJUSTED",
}

export enum TransactionStatus {
	INITIATED = "INITIATED",
	PENDING = "PENDING",
	SUCCESS = "SUCCESS",
	FAILED = "FAILED",
	CANCELLED = "CANCELLED",
}

export enum PaymentStatus {
	ORDER_NOT_CREATED = "ORDER_NOT_CREATED",
	ORDER_CREATED_PENDING_PAYMENT = "ORDER_CREATED_PENDING_PAYMENT",
	PAYMENT_IN_PROGRESS = "PAYMENT_IN_PROGRESS",
	PAYMENT_SUCCESS = "PAYMENT_SUCCESS",
	PAYMENT_FAILED_RETRY_ALLOWED = "PAYMENT_FAILED_RETRY_ALLOWED",
	PAYMENT_FAILED_REFUND_REQUESTED = "PAYMENT_FAILED_REFUND_REQUESTED",
	PAYMENT_FAILED_REFUND_PROCESSING = "PAYMENT_FAILED_REFUND_PROCE`SSING",
	PAYMENT_FAILED_REFUNDED = "PAYMENT_FAILED_REFUNDED",
	ORDER_CANCELLED = "ORDER_CANCELLED",
	ORDER_PARTIALLY_REFUNDED = "ORDER_PARTIALLY_REFUNDED",
	ORDER_FULLY_REFUNDED = "ORDER_FULLY_REFUNDED",
	ORDER_ADJUSTED = "ORDER_ADJUSTED",
	UNKNOWN = "UNKNOWN",
	SERVICE_UNAVAILABLE = "SERVICE_UNAVAILABLE",
	PAYMENT_FAILED = "PAYMENT_FAILED",
	// PAYMENT_CANCELLED = "PAYMENT_CANCELLED",
	// Loan Enums
	ORDER_CREATED_PENDING_LOAN_APPROVAL = "ORDER_CREATED_PENDING_LOAN_APPROVAL",
	ORDER_CREATED_LOAN_REJECTED = "ORDER_CREATED_LOAN_REJECTED",
	LOAN_APPROVED_PENDING_LOAN_DISBURSEMENT = "LOAN_APPROVED_PENDING_LOAN_DISBURSEMENT",
	LOAN_DISBURSEMENT_RECEIVED = "LOAN_DISBURSEMENT_RECEIVED",
	// Offline Payment Enums
	OFFLINE_PAYMENT_PENDING_VERIFICATION = "OFFLINE_PAYMENT_PENDING_VERIFICATION",
	OFFLINE_PAYMENT_APPROVED = "OFFLINE_PAYMENT_APPROVED",
	OFFLINE_PAYMENT_REJECTED = "OFFLINE_PAYMENT_REJECTED",
	OFFLINE_PAYMENT_REJECTED_SHOW_STATUS = "OFFLINE_PAYMENT_REJECTED_SHOW_STATUS",

	// Offline Loan Enums
	OFFLINE_LOAN_PENDING_VERIFICATION = "OFFLINE_LOAN_PENDING_VERIFICATION",
	OFFLINE_LOAN_APPROVED = "OFFLINE_LOAN_APPROVED",
	OFFLINE_LOAN_REJECTED = "OFFLINE_LOAN_REJECTED",
	OFFLINE_LOAN_REJECTED_SHOW_STATUS = "OFFLINE_LOAN_REJECTED_SHOW_STATUS",
}

export interface PaymentGateway {
	initPayment: (options: {
		orderId: string
		transactionId: string
		amount: number
		currency: string
		applicationId: string
		gatewayId: string
		access_key: string
		modeEazebuzz: string
		setValues: (values: any) => void
		onComplete?: (res: any) => void
		partnerId: string
		batchId: string
		mode: string
		key: string
		paymentMeta?: PAYMENT_META_RESPONSE
		setShowPaymentGateway: (showPaymentGateway: boolean) => void
		showPaymentGatewayRef: React.MutableRefObject<boolean>
	}) => Promise<void>
}

export interface CheckPaymentStatusResponse {
	status: string
	details: {
		loan_id?: string
		loan_amount?: number
		lender?: string
		loan_comments?: string
		loan_updated_at?: string
		order_id: string
		transaction_id: string
		updated_at: string
		amount: number
		currency: string
		plan: string
		payment_updated_at: string
		show_receipt: boolean
		paid_at: string
		comments: string
		reference_number: string
		payment_id: string
	}
	loan_details?: {
		loan_id: string
		application_id: string
		order_id: string
		batch_lender_course_id: string
		batch_id: string
		loan_application_id: string
		loan_amount: number
		sanctioned_amount: number
		currency: string
		interest_rate: number
		status: string
		credit_note_generated: boolean
		lender_ref_id: string
		meta_data: {
			source: string
		}
		agreement_url: string
		approved_at: string | null
		closed_at: string | null
		created_at: string
		updated_at: string
		applicant: {
			first_name: string
			last_name: string
			email: string
			country_code: string
			phone: string
			dob: string
			relationship: string
			additional_details: {
				applicantDetails: {
					employment_type: string
					marital_status: string
					monthly_income: number
					pan_number: string
					pincode: string
				}
				student_info: {
					first_name: string
					last_name: string
					dob: string
				}
				plan_id: string
			}
		}
		lender_id: string
		tenure_in_months: number
	}
}

export type LOAN_PROVIDER = {
	lender_id: string
	batch_id: string
	loan_type: string
	identifier: string
	tenures: {
		batch_lender_course_id: string
		lender_course_id: string
		tenure_in_months: number
		interest_rate: number
		lender_plan_id: string
	}[]
	merchant_id: string
	name: string
	display_name: string
}

export interface PaymentGatewayDetails {
	gateway: string
	credentials: {
		key?: string
	}
}
export interface PAYMENT_META_RESPONSE {
	lead_id: string
	full_name: string
	email: string
	phone: string
	university_name: string
}
export interface PAYMENT_VERIFIED_FIELDS {
	field_id: string
	is_verified: boolean
}
